import React from 'react'
import { css } from '@emotion/react'
import { array, object, string } from 'prop-types'

import mediaQueries from '@/layout/media-queries'
import FilterIcon from '@/assets/icons/filter.inline.svg'
import Collapse from '@/components/Collapse'

import FilterBadges from './FilterBadges'
import Filter from './Filter'
import Sort from './Sort'

const wrapperCss = (theme) => css`
  margin-bottom: 30px;
  position: sticky;
  top: 50px;
  z-index: 100;
  background-color: ${theme.colors.white};
  padding: 18px 0;
  border-bottom: 1px solid ${theme.colors.gray50};

  ${mediaQueries.mediumScreen()} {
    position: relative;
    top: 0px;
    background-color: initial;
    padding: 0;
    border: none;
  }

  .collapse__label-wrapper > div > div {
    width: 100%;

    ${mediaQueries.mediumScreen()} {
      width: auto;
    }

    h2 {
      margin: 0;
    }

    > span {
      display: none;
      font-size: 15px;
      line-height: 110%;
      opacity: 0.7;

      ${mediaQueries.mediumScreen()} {
        display: -webkit-inline-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        vertical-align: sub;
        margin-top: 7px;
        overflow: hidden;
      }
    }
  }
`

const sectionWrapperCss = (theme) => css`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;

  ${mediaQueries.mediumScreen()} {
    flex-wrap: nowrap;
  }

  h2 {
    font-size: 24px;
    line-height: 32px;
    word-break: break-word;
    font-weight: ${theme.font.bold};
  }

  > span {
    font-size: 15px;
    line-height: 110%;
  }
`

const filtersListCss = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 100%;
  padding: 22px 0;
  gap: 12px;
`

const filtersWrapperCss = (theme) => css`
  margin-top: 15px;
  border-top: 1px solid ${theme.colors.grayDivider};
  border-bottom: 1px solid ${theme.colors.grayDivider};
`

const filterBtnCss = (isSecondaryBtn) => (theme) =>
  css`
    cursor: pointer;
    padding: 2.5px;
    margin-left: 16px;

    svg {
      position: relative;
      top: -1px;

      > path {
        fill: ${isSecondaryBtn ? theme.colors.primary : theme.colors.blue500};
      }
    }
  `

const rightSectionCss = css`
  display: flex;
  align-items: center;
  margin-top: 15px;
  margin-left: 0;

  ${mediaQueries.mediumScreen()} {
    margin-left: 20px;
  }
`

const buttonsWrapperCss = css`
  display: flex;
  gap: 16px;
  align-items: center;
`

const verticalDividerCss = (theme) => css`
  display: block;
  height: 28px;
  min-width: 1px;
  background-color: ${theme.colors.gray500};
`

const ListHeader = ({
  storageKey,
  title,
  sort,
  filters,
  subtitle,
  ...rest
}) => {
  const hasFilters = !!filters?.length

  return (
    <Collapse
      storageKey={storageKey}
      hasFilters={hasFilters}
      css={wrapperCss}
      labelComponent={({ inputRef, isContentVisible }) => {
        const handleOnClick = () => {
          inputRef.current.click()
        }

        return (
          <div css={sectionWrapperCss}>
            <div data-test="listHeaderWrapper">
              <h2>{title}</h2>
              <span>{subtitle}</span>
            </div>
            <div css={rightSectionCss}>
              <FilterBadges filters={filters} />

              <div css={buttonsWrapperCss} data-test="categoryFiltersButton">
                {hasFilters && (
                  <div
                    css={filterBtnCss(!isContentVisible)}
                    onClick={handleOnClick}
                    data-test="filter-icon"
                  >
                    <FilterIcon />
                  </div>
                )}

                {sort && (
                  <>
                    {hasFilters && <div css={verticalDividerCss} />}
                    <Sort {...sort} />
                  </>
                )}
              </div>
            </div>
          </div>
        )
      }}
      content={
        <div css={filtersWrapperCss}>
          <div css={filtersListCss}>
            {filters?.map((filter) => (
              <Filter key={filter.id} {...filter} />
            ))}
          </div>
        </div>
      }
      {...rest}
    />
  )
}

ListHeader.propTypes = {
  filters: array,
  sort: object,
  title: string,
  subtitle: string,
  storageKey: string,
}

export default ListHeader
